import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ApiService } from 'src/services/api.service';
import { ReviewerPayload, toReviewerPayload } from 'src/app/pages/create/shared/assignee-picker/reviewer-payload';
import { SequentialReviewPickerHostComponent } from './sequential-review-picker-host.component';

export interface SequentialReviewDialogData {
  mode: 'add' | 'edit';
  task: any;
}

export interface SequentialReviewDialogResult {
  taskName: string;
  /** Full assignee-picker contract entries for the task's reviewers[] node. */
  reviewers: ReviewerPayload[];
}

/**
 * Plan #33 fase 4 — provisional UI (until Dani designs the drawer version):
 * dialog behind the "Add review after this one" / "Edit reviewers" actions of
 * the legacy task panel. Reviewers come EXCLUSIVELY from the shared assignee
 * picker (decision David 2026-09-04) — externals go through the picker's own
 * Invite tab — and the picker opens with the skill/language of the review
 * window being extended (resolved server-side by /reviewPickerContext).
 */
@Component({
  selector: 'tan-sequential-review-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  template: `
    <h2 mat-dialog-title>{{ data.mode === 'add' ? 'Add review after this one' : 'Edit reviewers' }}</h2>

    <mat-dialog-content class="srd-content">
      <mat-form-field *ngIf="data.mode === 'add'" appearance="outline" class="srd-field">
        <mat-label>Review name</mat-label>
        <input matInput [(ngModel)]="taskName" />
      </mat-form-field>

      <div class="srd-list" *ngIf="reviewersList.length">
        <div class="srd-item" *ngFor="let r of reviewersList; let i = index">
          <mat-icon class="srd-item-icon">person</mat-icon>
          <span class="srd-item-text">{{ r.name }} · {{ r.email }}</span>
          <button mat-icon-button (click)="removeReviewer(i)" matTooltip="Remove">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>

      <div class="srd-picker-row">
        <button mat-stroked-button (click)="openPicker()">
          <mat-icon>person_search</mat-icon>
          {{ reviewersList.length ? 'Add another reviewer' : 'Search reviewer' }}
        </button>
      </div>
      <div class="srd-error" *ngIf="pickerError">{{ pickerError }}</div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cancel</button>
      <button mat-flat-button color="primary" [disabled]="!reviewersList.length" (click)="save()">
        {{ data.mode === 'add' ? 'Add review' : 'Save' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .srd-content { display: flex; flex-direction: column; gap: 4px; min-width: 380px; }
    .srd-field { width: 100%; }
    .srd-picker-row { display: flex; align-items: center; gap: 12px; margin-bottom: 4px; }
    .srd-list { display: flex; flex-direction: column; gap: 2px; margin-bottom: 12px; }
    .srd-item { display: flex; align-items: center; gap: 8px; padding: 2px 4px; border-radius: 6px; background: rgba(0, 0, 0, 0.04); }
    .srd-item-icon { font-size: 18px; width: 18px; height: 18px; color: var(--text-secondary, #6b7280); }
    .srd-item-text { flex: 1; font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .srd-error { font-size: 12px; color: #b91c1c; margin-bottom: 8px; }
  `]
})
export class SequentialReviewDialogComponent {
  taskName = '';
  reviewersList: ReviewerPayload[] = [];
  pickerError = '';
  private taskData: any = {};
  private pickerContext: { skillKey: string | null; sourceLang: string | null; targetLangs: string[] } | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SequentialReviewDialogData,
    private dialogRef: MatDialogRef<SequentialReviewDialogComponent>,
    private dialog: MatDialog,
    private apiService: ApiService
  ) {
    // Chain rounds carry a counter suffix (Dani 2026-09-04): suggest the next
    // one from the extended review's name; the backend recomputes it (unique
    // within the project) if the field is left empty or unchanged.
    const baseName = (data.task?.taskName || '').trim();
    if (data.mode === 'add') {
      const numbered = baseName.match(/^(.*?)\s*-\s*(\d+)$/);
      this.taskName = numbered
        ? `${numbered[1].trim()} - ${Number(numbered[2]) + 1}`
        : (baseName ? `${baseName} - 2` : '');
    } else {
      this.taskName = baseName;
    }
    try {
      this.taskData = JSON.parse(data.task?.data || '{}');
    } catch { this.taskData = {}; }
    if (data.mode === 'edit') {
      // reviewers[] carries full contract entries; the fallbacks cover chain
      // reviews created before it did (plain string / reviewerName key).
      const reviewers = this.taskData.reviewers;
      if (Array.isArray(reviewers) && reviewers.length > 0 && typeof reviewers[0] === 'object') {
        this.reviewersList = reviewers.filter((r: any) => r && r.email);
      } else if (this.taskData.emailTo) {
        this.reviewersList = [this.invitePayload(
          this.taskData.reviewerName
            || (Array.isArray(reviewers) && reviewers.length > 0 ? String(reviewers[0]) : ''),
          String(this.taskData.emailTo))];
      }
    }

    // Skill + languages of the review window being extended, resolved
    // server-side (the review's own data may not carry the language — LUMO
    // keeps it in the root workflow).
    this.apiService.getReviewPickerContext(data.task.id).subscribe({
      next: (ctx) => { this.pickerContext = ctx; },
      error: () => { this.pickerContext = null; },
    });
  }

  openPicker(): void {
    this.pickerError = '';
    const ctx = this.pickerContext;
    // Fallback when the context has not arrived / errored: same derivation as
    // before, so the picker is always usable.
    const fallbackLang = this.taskData.reviewerLanguage || this.taskData.targetLanguage
      || this.taskData.language || 'en';
    const targetLangs = ctx?.targetLangs?.length ? ctx.targetLangs : [String(fallbackLang)];

    this.dialog.open(SequentialReviewPickerHostComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      hasBackdrop: false,
      panelClass: 'srd-picker-dialog-panel',
      data: {
        targetLangs,
        skillKey: ctx?.skillKey || undefined,
        sourceLang: ctx?.sourceLang || undefined,
      }
    }).afterClosed().subscribe((person) => this.onPickerAssign(person));
  }

  onPickerAssign(person: any): void {
    if (!person) { return; }
    const email = (person.email || '').trim().toLowerCase();
    if (!email) {
      this.pickerError = `${person.name || 'The selected person'} has no email on file — add them through the picker's Invite tab instead.`;
      return;
    }
    const reviewer: ReviewerPayload = { ...toReviewerPayload(person), email };
    this.reviewersList = this.reviewersList
      .filter((r) => (r.email || '').toLowerCase() !== email)
      .concat(reviewer);
  }

  removeReviewer(index: number): void {
    this.reviewersList = this.reviewersList.filter((_, i) => i !== index);
  }

  save(): void {
    if (this.reviewersList.length === 0) { return; }

    const result: SequentialReviewDialogResult = {
      taskName: this.taskName.trim(),
      reviewers: this.reviewersList,
    };
    this.dialogRef.close(result);
  }

  private invitePayload(name: string, email: string): ReviewerPayload {
    const clean = email.trim().toLowerCase();
    return {
      status: 'ASSIGNED', source: 'invite', name: name || clean, email: clean,
      peopleId: null, organizationId: null, prospectId: null, engagement: null,
      rate: null, rateUnit: null, currency: null, totalPrice: null,
    };
  }
}
